import React, { useState, useEffect, useCallback } from "react";
import {
  AppBar,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography
} from '@mui/material';
import { makeStyles } from '@Compat/makeStyles';
import { siteTokens } from "../siteTokens";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette } from '@fortawesome/free-solid-svg-icons/faPalette';
import { faBars } from '@fortawesome/free-solid-svg-icons/faBars';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
// 360 px WebP: the logo is shown at most 180 px wide here (the brochure
// keeps the full-size PNG for print).
import Logo from "../assets/logo_header.webp";
import LogoWhite from "../assets/logo_header_white.webp";
import SiteLanguageMenu from "./SiteLanguageMenu";
import { IRoutes } from "../../../routesEnum";
import { track } from "../../../core/analytics/ga4";
import { useSiteContent } from "../SiteContentContext";
import { useColorMode } from "../ColorModeContext";
import ThemePickerPanel from "@Components/theme/ThemePickerPanel";
import { useTranslation } from "react-i18next";
import { scrollToSiteSection, SECTION_RENDERED_EVENT } from "../siteSections";

/** Fidava Sports (associations and clubs) has its own site. */
const SPORTS_SITE_URL = "https://sports.fidava-pt.com";

const useStyles = makeStyles((theme) => {
  const tk = siteTokens(theme);
  return {
  logo: {
    width: 180,
    height: "auto",
    transition: "width 220ms ease",
    [theme.breakpoints.down("sm")]: {
      width: 130,
      height: "auto"
    },
    "@media (max-width: 359.95px)": {
      width: 108
    }
  },
  logoShrunk: {
    width: 148,
    [theme.breakpoints.down("sm")]: {
      width: 116
    }
  },
  toolbar: {
    width: "100%",
    maxWidth: 1400,
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 4),
    gap: theme.spacing(1),
    minHeight: 72,
    transition: "min-height 220ms ease",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 2),
      minHeight: 60
    }
  },
  toolbarShrunk: {
    minHeight: 56,
    [theme.breakpoints.down("sm")]: {
      minHeight: 52
    }
  },
  appBar: {
    // Fully opaque in dark mode — the previous 0.78 alpha + 12px
    // backdrop blur was sampling whatever sat behind the navbar and
    // tinting on hover (the Pain Points card glow leaked through as
    // a coloured halo across the top edge of the topbar). High alpha
    // in light mode for the same reason. Follows the selected theme's page
    // colour, opaque on every theme (no backdrop-filter).
    backgroundColor: tk.page,
    backdropFilter: "none",
    WebkitBackdropFilter: "none",
    boxShadow: `0 1px 0 ${tk.inkA(0.06)}`,
    padding: theme.spacing(0, 2),
    transition: "background-color 220ms ease, box-shadow 220ms ease",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0, 1.5)
    }
  },
  appBarScrolled: {
    backgroundColor: tk.surface,
    boxShadow: tk.dark
      ? "0 6px 24px rgba(0,0,0,0.32)"
      : `0 6px 24px ${tk.accentDeepA(0.08)}`
  },
  // Desktop nav links — hidden on small viewports, replaced by the
  // hamburger + drawer.
  navLinks: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    [theme.breakpoints.down("md")]: {
      display: "none"
    }
  },
  navLink: {
    position: "relative",
    padding: theme.spacing(0.8, 2),
    borderRadius: 999,
    fontWeight: 600,
    fontSize: "0.95rem",
    textTransform: "none",
    color: theme.palette.text.primary,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    transition: "color 180ms ease, background 180ms ease",
    "&:hover": {
      color: tk.accent,
      background: tk.accentA(0.06)
    },
    // Underline sits under the label, scaled from the center — mirrors
    // the "nav spy" pattern of tracking which section is currently
    // furthest into view rather than just first-to-enter/last-to-leave.
    "&::after": {
      content: "''",
      position: "absolute",
      left: theme.spacing(2),
      right: theme.spacing(2),
      bottom: 4,
      height: 2,
      borderRadius: 2,
      background: tk.accent,
      transform: "scaleX(0)",
      transformOrigin: "center",
      transition: "transform 220ms ease"
    }
  },
  navLinkActive: {
    color: tk.accent,
    "&::after": {
      transform: "scaleX(1)"
    }
  },
  // Desktop action group — shows theme toggle + flag + Sign in +
  // Get started. On small viewports, only the gradient CTA + the
  // hamburger remain visible; everything else moves into the drawer.
  desktopActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    // Hide the full desktop action group at <md (900px). The bar had
    // 8 elements crammed into 768px on tablet; collapsing earlier
    // gives the hamburger room to take over.
    [theme.breakpoints.down("md")]: {
      display: "none"
    }
  },
  mobileActions: {
    display: "none",
    alignItems: "center",
    gap: theme.spacing(0.5),
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      display: "flex"
    }
  },
  mobileCta: {
    "&&": {
      padding: theme.spacing(0.8, 2),
      borderRadius: 999,
      fontWeight: 700,
      fontSize: "0.88rem",
      textTransform: "none",
      color: tk.onAccent,
      background: tk.gradient(135),
      boxShadow: `0 8px 20px ${tk.accentDeepA(0.32)}`,
      whiteSpace: "nowrap",
      minWidth: 0
    }
  },
  // Compact outlined Login affordance for tablet/mobile. Previously
  // the login button only lived inside `desktopActions` (hidden at
  // <md), forcing tablet visitors with an account to open the
  // hamburger to find a way in. Keeping the button small + outlined
  // so the gradient "Iniciar" stays the dominant CTA in the row.
  mobileLogin: {
    "&&": {
      padding: theme.spacing(0.6, 1.6),
      borderRadius: 999,
      fontWeight: 600,
      fontSize: "0.85rem",
      textTransform: "none",
      color: theme.palette.text.primary,
      background: "transparent",
      border: `1.5px solid ${tk.inkA(0.32)}`,
      whiteSpace: "nowrap",
      minWidth: 0,
      "&:hover": {
        borderColor: tk.accent,
        color: tk.accent
      },
      // Phones: logo + Sign in + CTA + menu didn't fit (EN/DE/FR labels push
      // the menu icon off-screen). Sign in is also in the menu drawer. (Was
      // breakpoints.down("xs"), which never matches — xs starts at 0.)
      "@media (max-width: 419.95px)": {
        display: "none"
      }
    }
  },
  subscribeButton: {
    "&&": {
      position: "relative",
      overflow: "hidden",
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      borderRadius: 999,
      fontWeight: 700,
      textTransform: "none",
      color: tk.onAccent,
      background: tk.gradient(135),
      boxShadow: `0 12px 24px ${tk.accentDeepA(0.32)}`,
      transition: "transform 200ms ease, box-shadow 200ms ease",
      "&::before": {
        content: "''",
        position: "absolute",
        top: 0,
        left: "-150%",
        width: "75%",
        height: "100%",
        background: "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)",
        transform: "skewX(-22deg)",
        transition: "left 700ms ease",
        pointerEvents: "none"
      },
      "&:hover": {
        background: tk.gradient(135),
        transform: "translateY(-1px)",
        boxShadow: `0 16px 32px ${tk.accentDeepA(0.42)}`
      },
      "&:hover::before": {
        left: "150%"
      }
    }
  },
  loginButton: {
    "&&": {
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(2.5),
      borderRadius: 999,
      fontWeight: 600,
      fontSize: "0.95rem",
      textTransform: "none",
      color: theme.palette.text.primary,
      background: "transparent",
      border: `1.5px solid ${tk.inkA(0.32)}`,
      boxShadow: "none",
      "&:hover": {
        background: tk.accentA(0.06),
        borderColor: tk.accent,
        color: tk.accent
      }
    }
  },
  // Deliberately a plain text link, not a bordered/filled button — the
  // topbar already has 4 elements at md+ (theme toggle, language, Sign
  // in, the gradient CTA); a 5th button competing visually with the
  // primary "Get started" CTA would just add noise. This one is for
  // discoverability (reaching /partner/apply without scrolling to find
  // the on-page section), not for competing as a second call to action.
  partnerButton: {
    "&&": {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      borderRadius: 999,
      fontWeight: 600,
      fontSize: "0.88rem",
      textTransform: "none",
      color: theme.palette.text.secondary,
      background: "transparent",
      whiteSpace: "nowrap",
      "&:hover": {
        background: tk.accentA(0.06),
        color: tk.accent
      }
    }
  },
  themeToggle: {
    color: theme.palette.text.primary,
    transition: "background 180ms ease, transform 180ms ease",
    "&:hover": {
      background: tk.accentA(0.06),
      transform: "rotate(8deg)"
    }
  },
  drawerPaper: {
    width: 320,
    maxWidth: "85vw",
    background: theme.palette.background.default,
    padding: theme.spacing(2.5, 2, 3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  drawerLogo: {
    width: 130,
    height: "auto"
  },
  drawerList: {
    padding: 0
  },
  drawerLink: {
    borderRadius: 12,
    margin: theme.spacing(0.5, 0),
    padding: theme.spacing(1.25, 2),
    "&:hover": {
      background: tk.accentA(0.08)
    }
  },
  drawerLinkText: {
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  drawerActions: {
    marginTop: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5)
  },
  drawerExtras: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: theme.spacing(1)
  },
  drawerLogin: {
    "&&": {
      padding: theme.spacing(1.2, 2),
      borderRadius: 999,
      fontWeight: 600,
      textTransform: "none",
      color: theme.palette.text.primary,
      border: `1.5px solid ${tk.inkA(0.32)}`
    }
  },
  drawerSubscribe: {
    "&&": {
      padding: theme.spacing(1.4, 2),
      borderRadius: 999,
      fontWeight: 700,
      textTransform: "none",
      color: tk.onAccent,
      background: tk.gradient(135),
      boxShadow: `0 12px 24px ${tk.accentDeepA(0.32)}`
    }
  }
  };
});

interface HeaderA11y {
  lightMode?: string;
  darkMode?: string;
  openMenu?: string;
  closeMenu?: string;
}

interface NavItem {
  label: string;
  section: string;
}

const Header: React.FC = () => {
  const classes = useStyles();
  const [isScrolled, setIsScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const history = useHistory();
  const { content } = useSiteContent();
  const { mode, themeName, setThemeName } = useColorMode();
  // Same theme catalogue and picker as the app (ThemePicker namespace is
  // registered in boot-marketing).
  const { t: tTheme } = useTranslation("ThemePicker");
  const [themeAnchor, setThemeAnchor] = useState<HTMLElement | null>(null);
  const openThemePicker = (event: React.MouseEvent<HTMLElement>) => setThemeAnchor(event.currentTarget);
  const headerContent = (content as { header?: { logoAlt?: string; subscribeLabel?: string; loginLabel?: string; becomePartnerLabel?: string; sportsLabel?: string; nav?: NavItem[]; a11y?: HeaderA11y } }).header ?? {};
  // Screen-reader labels, in the page language (English fallback).
  const a11y = headerContent.a11y ?? {};
  const themeLabel = tTheme("tooltip", { defaultValue: "Theme" });
  const logoAlt = headerContent.logoAlt ?? content.footer?.brand?.name ?? "";
  // Dark-theme topbar lands on a dark gradient → use the white-text
  // wordmark; light-theme keeps the standard dark-text variant.
  const logoSrc = mode === "dark" ? LogoWhite : Logo;
  const subscribeLabel = headerContent.subscribeLabel ?? content.hero?.secondaryCta?.label ?? "";
  const loginLabel = headerContent.loginLabel ?? "Sign in";
  const becomePartnerLabel = headerContent.becomePartnerLabel;
  // Fidava Sports, the club product, lives on its own site.
  const sportsLabel = headerContent.sportsLabel;
  const navItems = Array.isArray(headerContent.nav) ? headerContent.nav : [];
  const [activeSection, setActiveSection] = useState<string | null>(null);
  // Stable dependency for the nav-spy effect below — navItems itself is a
  // new array reference every render (derived fresh from content), which
  // would otherwise tear down and recreate the IntersectionObserver on
  // every unrelated re-render (theme toggle, scroll state, ...).
  const navSectionsKey = navItems.map((item) => item.section).join("|");

  useEffect(() => {
    const handleScroll = () => {
      // The site scrolls inside VerticalScroller's container, not the window —
      // read its scrollTop so the shrink-on-scroll still fires on the
      // marketing site. The topbar itself stays visible at all times.
      const inner = document.querySelector<HTMLElement>("[data-site-scroller]");
      const scrollTop = Math.max(window.scrollY, inner?.scrollTop ?? 0);
      setIsScrolled(scrollTop > 24);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    const inner = document.querySelector<HTMLElement>("[data-site-scroller]");
    inner?.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      inner?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Sections below the hero mount lazily, so the spy re-wires each time one
  // renders (otherwise it would only ever observe the ones already there).
  const [renderedSections, setRenderedSections] = useState(0);
  useEffect(() => {
    const bump = () => setRenderedSections((v) => v + 1);
    window.addEventListener(SECTION_RENDERED_EVENT, bump);
    return () => window.removeEventListener(SECTION_RENDERED_EVENT, bump);
  }, []);

  // Nav "spy" underline — highlights whichever section is currently
  // furthest into view, not just the first one to enter or last to
  // leave. Keeps a live map of each section's intersection ratio (rather
  // than reacting to a single entry in isolation) so the underline
  // doesn't flicker off while the visitor is scrolling between two
  // sections that are both partially visible.
  useEffect(() => {
    if (!navSectionsKey || typeof IntersectionObserver === "undefined") {
      return;
    }

    const targets = navSectionsKey
      .split("|")
      .map((section) => ({
        section,
        el: document.querySelector<HTMLElement>(`[data-site-section='${section}']`)
      }))
      .filter((target): target is { section: string; el: HTMLElement } => Boolean(target.el));

    if (!targets.length) {
      return;
    }

    const ratios = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const match = targets.find((target) => target.el === entry.target);
          if (!match) {
            return;
          }
          if (entry.isIntersecting) {
            ratios.set(match.section, entry.intersectionRatio);
          } else {
            ratios.delete(match.section);
          }
        });

        let bestSection: string | null = null;
        let bestRatio = 0;
        ratios.forEach((ratio, section) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestSection = section;
          }
        });
        setActiveSection(bestSection);
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: [0, 0.2, 0.6] }
    );

    targets.forEach((target) => observer.observe(target.el));
    return () => observer.disconnect();
  }, [navSectionsKey, renderedSections]);

  const handleSubscribeClick = useCallback(() => {
    setDrawerOpen(false);
    track("start_click", { location: "header" });
    history.push(IRoutes.wizard);
  }, [history]);

  const handleLoginClick = useCallback(() => {
    setDrawerOpen(false);
    history.push(IRoutes.loginPage);
  }, [history]);

  const handleBecomePartnerClick = useCallback(() => {
    setDrawerOpen(false);
    track("partner_click", { location: "header" });
    history.push(IRoutes.becomePartner);
  }, [history]);

  const handleNavClick = useCallback((section: string) => {
    setDrawerOpen(false);
    scrollToSiteSection(section);
  }, []);

  return (
    <>
      <AppBar
        position="fixed"
        className={`${classes.appBar}${isScrolled ? ` ${classes.appBarScrolled}` : ""}`}
      >
        <Toolbar
          className={`${classes.toolbar}${isScrolled ? ` ${classes.toolbarShrunk}` : ""}`}
          disableGutters
        >
          <Typography variant="h6" component="div">
            <img
              src={logoSrc}
              alt={logoAlt}
              className={`${classes.logo}${isScrolled ? ` ${classes.logoShrunk}` : ""}`}
              width={400}
              height={226}
            />
          </Typography>
          {navItems.length ? (
            <nav className={classes.navLinks}>
              {navItems.map((item) => (
                <button
                  key={item.section}
                  type="button"
                  className={`${classes.navLink}${activeSection === item.section ? ` ${classes.navLinkActive}` : ""}`}
                  aria-current={activeSection === item.section ? "true" : undefined}
                  onClick={() => handleNavClick(item.section)}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          ) : null}
          {/* Desktop actions (sm+) — full row */}
          <div className={classes.desktopActions}>
            {sportsLabel ? (
              <Button variant="text" className={classes.partnerButton} href={SPORTS_SITE_URL} data-ga-event="site_fidava_sports">
                {sportsLabel}
              </Button>
            ) : null}
            {becomePartnerLabel ? (
              <Button
                variant="text"
                className={classes.partnerButton}
                onClick={handleBecomePartnerClick}
              >
                {becomePartnerLabel}
              </Button>
            ) : null}
            <IconButton
              className={classes.themeToggle}
              size="small"
              onClick={openThemePicker}
              aria-label={themeLabel}
              aria-haspopup="dialog"
              title={themeLabel}
            >
              <FontAwesomeIcon icon={faPalette} style={{ fontSize: 20 }} />
            </IconButton>
            <SiteLanguageMenu />
            <Button
              variant="text"
              className={classes.loginButton}
              onClick={handleLoginClick}
            >
              {loginLabel}
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.subscribeButton}
              onClick={handleSubscribeClick}
            >
              {subscribeLabel}
            </Button>
          </div>
          {/* Mobile actions — Login (outlined) + gradient CTA + hamburger */}
          <div className={classes.mobileActions}>
            <Button
              variant="outlined"
              className={classes.mobileLogin}
              onClick={handleLoginClick}
            >
              {loginLabel}
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.mobileCta}
              onClick={handleSubscribeClick}
            >
              {subscribeLabel}
            </Button>
            <IconButton
              size="small"
              className={classes.themeToggle}
              onClick={() => setDrawerOpen(true)}
              aria-label={a11y.openMenu ?? "Open menu"}
            >
              <FontAwesomeIcon icon={faBars} />
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ className: classes.drawerPaper }}
      >
        <div className={classes.drawerHeader}>
          <img src={logoSrc} alt={logoAlt} className={classes.drawerLogo} />
          <IconButton size="small" onClick={() => setDrawerOpen(false)} aria-label={a11y.closeMenu ?? "Close menu"}>
            <FontAwesomeIcon icon={faXmark} style={{ fontSize: 20 }} />
          </IconButton>
        </div>
        <Divider />
        <List className={classes.drawerList}>
          {navItems.map((item) => (
            <ListItemButton
              key={item.section}
              className={classes.drawerLink}
              onClick={() => handleNavClick(item.section)}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ className: classes.drawerLinkText }}
              />
            </ListItemButton>
          ))}
        </List>
        {sportsLabel ? (
          <ListItemButton className={classes.drawerLink} component="a" href={SPORTS_SITE_URL}>
            <ListItemText
              primary={sportsLabel}
              primaryTypographyProps={{ className: classes.drawerLinkText }}
            />
          </ListItemButton>
        ) : null}
        {becomePartnerLabel ? (
          <ListItemButton className={classes.drawerLink} onClick={handleBecomePartnerClick}>
            <ListItemText
              primary={becomePartnerLabel}
              primaryTypographyProps={{ className: classes.drawerLinkText }}
            />
          </ListItemButton>
        ) : null}
        <div className={classes.drawerExtras}>
          <IconButton
            className={classes.themeToggle}
            size="small"
            onClick={openThemePicker}
            aria-label={themeLabel}
            aria-haspopup="dialog"
          >
            <FontAwesomeIcon icon={faPalette} style={{ fontSize: 20 }} />
          </IconButton>
          <SiteLanguageMenu />
        </div>
        <div className={classes.drawerActions}>
          <Button
            variant="outlined"
            fullWidth
            className={classes.drawerLogin}
            onClick={handleLoginClick}
          >
            {loginLabel}
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            className={classes.drawerSubscribe}
            onClick={handleSubscribeClick}
          >
            {subscribeLabel}
          </Button>
        </div>
      </Drawer>
      <ThemePickerPanel
        anchorEl={themeAnchor}
        onClose={() => setThemeAnchor(null)}
        value={themeName}
        onSelect={(name) => {
          setThemeName(name);
          setThemeAnchor(null);
        }}
      />
    </>
  );
};

export default Header;
